import { createQuery } from 'react-query-kit';
import { keepPreviousData } from '@tanstack/react-query';
import { useIsMounted } from '@toss/react';

import { countQuizsetsBySubject, type QuizsetFilterCriteria } from '@/api';
import { usePublicSuggestionFilterPayload } from '@/module/public/hooks/usePublicSuggestionFilterPayload';

export const useFindSubjectGroupCount = () => {
  const { categoryFilters, enabled } = usePublicSuggestionFilterPayload();
  const isMounted = useIsMounted();

  const { isLoading, isRefetching, data } = useFetchSubjectGroupCount({
    variables: categoryFilters,
    throwOnError: true,
    placeholderData: keepPreviousData,
    enabled: isMounted && enabled,
  });

  return (categoryGroupId: string, isSubSubject: boolean) => {
    if (isRefetching || isLoading) return '';
    if (isSubSubject) {
      const subSubGroupId = categoryGroupId.slice(2);
      return data?.data.subSubjectCounts[subSubGroupId] ?? 0;
    }

    return data?.data.subjectCounts[categoryGroupId] ?? 0;
  };
};

const useFetchSubjectGroupCount = createQuery<
  Awaited<ReturnType<typeof countQuizsetsBySubject>>,
  QuizsetFilterCriteria
>({
  queryKey: ['count-quizsets-by-subject'],
  fetcher: variables => {
    return countQuizsetsBySubject(variables, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
  // 좌측 필터(SubjectAccordion)와 검색 추천 패널이 이 쿼리를 공유한다.
  // 기본 staleTime(0)이면 카운트가 항상 stale 로 취급돼, 추천 패널이 마운트되며
  // enabled false→true 로 전환될 때(검색창 포커스)마다 동일 variables 인데도
  // refetch 가 발생한다. 그 동안 카운트가 공백으로 깜빡이고 서버 호출도 중복된다.
  //
  // 카운트는 시간으로 만료시킬 필요가 없고, "입력이 바뀔 때만" 갱신되면 된다.
  // staleTime: Infinity 로 두면 queryKey(=필터 variables) 가 바뀔 때만 다시
  // 조회하고, 그 외(포커스/마운트/창 포커스)에는 캐시 값을 그대로 보여준다.
  staleTime: Infinity,
});
